import { Component } from '@theme/component';
import { prefersReducedMotion, clamp } from '@theme/utilities';

/*
  Science side page: scroll reveal + two SVG chart components.

  <science-reveal-component>        wraps the article, reveals [data-reveal] descendants on scroll
  <science-bar-chart-component>     horizontal/vertical range bar chart (packaging mass, carbon)
  <science-carbon-chart-component>  interactive cumulative carbon model (glass / ceramic / plastic)

  Everything renders plain inline SVG so it matches the theme's typography and colours and
  needs no charting library. All SVG is rebuilt on resize, so nothing here relies on
  viewBox scaling (which would also scale the text).
*/

const COLORS = {
  ink: '#3A1B0F',
  bar: '#4072AA',
  barHighlight: '#ABB3DC',
  trigly: '#4072AA',
  pouch: '#535E41',
  aluminium: '#B3976E',
};

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Escape a string for use inside markup built with template literals.
 * @param {string | number} value
 */
const esc = (value) =>
  String(value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/**
 * Lazily create the HTML tooltip used by both charts. Positioned relative to the host.
 * @param {HTMLElement} host
 */
function getTooltip(host) {
  let tip = host.querySelector(':scope > .sci-tooltip');
  if (!(tip instanceof HTMLElement)) {
    tip = document.createElement('div');
    tip.className = 'sci-tooltip';
    tip.setAttribute('role', 'status');
    tip.hidden = true;
    host.append(tip);
  }
  return tip;
}

/**
 * Place a tooltip near an anchor point (host-relative px), flipping to stay inside the host.
 * @param {HTMLElement} host
 * @param {HTMLElement} tip
 * @param {number} x
 * @param {number} y
 */
function placeTooltip(host, tip, x, y) {
  tip.hidden = false;
  const tipWidth = tip.offsetWidth;
  const tipHeight = tip.offsetHeight;
  let left = x + 12;
  if (left + tipWidth > host.clientWidth - 4) left = Math.max(4, x - tipWidth - 12);
  const top = Math.max(4, Math.min(y - tipHeight / 2, host.clientHeight - tipHeight - 4));
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

/**
 * Pick a "nice" gridline step (1/2/5 × 10^k) whose line count is closest to `target`.
 * @param {number} max
 * @param {number} target
 * @returns {number[]} tick values strictly inside (0, max]
 */
function niceTicks(max, target) {
  if (!(max > 0)) return [];
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const candidates = [];
  for (const m of [magnitude / 10, magnitude, magnitude * 10]) {
    for (const f of [1, 2, 5]) candidates.push(m * f);
  }
  // Gridlines stay sparse: take the step giving the most lines without exceeding the target.
  // A single lonely line is worse than one line over target, so allow target + 1 in that case.
  const counted = candidates.map((step) => ({ step, count: Math.floor(max / step) })).filter((c) => c.count >= 1);
  const within = (/** @type {number} */ limit) =>
    counted.filter((c) => c.count <= limit).sort((a, b) => b.count - a.count)[0];
  let choice = within(target);
  if (!choice || choice.count <= 1) choice = within(target + 1) ?? choice;
  if (!choice) choice = counted.sort((a, b) => a.count - b.count)[0];
  const best = choice?.step ?? max;
  const ticks = [];
  for (let v = best; v <= max + 1e-9; v += best) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

/**
 * Format a number with thin thousands separators for axis labels.
 * @param {number} value
 */
const formatNumber = (value) => value.toLocaleString('en-GB', { maximumFractionDigits: 2 });

/* -------------------------------------------------------------------------- */
/* Scroll reveal                                                              */
/* -------------------------------------------------------------------------- */

class ScienceReveal extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

    this.setAttribute('data-ready', '');
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          this.#observer?.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    window.addEventListener('beforeprint', this.#onBeforePrint);
    window.addEventListener('afterprint', this.#onAfterPrint);

    const targets = this.querySelectorAll('[data-reveal]');
    targets.forEach((target, index) => {
      // Siblings inside a grid stagger via --i (see science-side-styles).
      const parent = target.parentElement;
      if (parent?.hasAttribute('data-reveal-stagger')) {
        const i = Array.from(parent.children).indexOf(target);
        /** @type {HTMLElement} */ (target).style.setProperty('--i', String(i));
      }
      // Anything already above the fold reveals immediately so the page never loads blank.
      if (index < 3) target.classList.add('is-visible');
      else this.#observer?.observe(target);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    window.removeEventListener('beforeprint', this.#onBeforePrint);
    window.removeEventListener('afterprint', this.#onAfterPrint);
  }

  /** Collapsibles we opened for printing, closed again afterwards. @type {HTMLDetailsElement[]} */
  #openedForPrint = [];

  /* Paper can't scroll or click: reveal everything and open every methodology note. */
  #onBeforePrint = () => {
    for (const target of this.querySelectorAll('[data-reveal]')) target.classList.add('is-visible');
    this.#openedForPrint = [];
    for (const details of this.querySelectorAll('details')) {
      if (!(details instanceof HTMLDetailsElement) || details.open) continue;
      details.open = true;
      this.#openedForPrint.push(details);
    }
  };

  #onAfterPrint = () => {
    for (const details of this.#openedForPrint) details.open = false;
    this.#openedForPrint = [];
  };
}

if (!customElements.get('science-reveal-component')) {
  customElements.define('science-reveal-component', ScienceReveal);
}

/* -------------------------------------------------------------------------- */
/* Range bar chart                                                            */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {{ label: string, low: number, high?: number, highlight?: boolean }} BarRow
 */

class ScienceBarChart extends Component {
  /** @type {BarRow[]} */
  #rows = [];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    const data = this.querySelector('script[type="application/json"]');
    try {
      this.#rows = data ? JSON.parse(data.textContent ?? '[]') : [];
    } catch {
      this.#rows = [];
    }
    this.#resizeObserver = new ResizeObserver(() => this.#maybeRender());
    this.#resizeObserver.observe(this);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    this.#maybeRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  /** @param {Event} event */
  #hitFromEvent(event) {
    const target = event.target;
    return target instanceof Element ? target.closest('.sci-chart-hit') : null;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    const hit = this.#hitFromEvent(event);
    if (hit) this.#showValue(hit);
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    const hit = this.#hitFromEvent(event);
    if (!hit) return;
    const next = event.relatedTarget;
    if (next instanceof Element && next.closest('.sci-chart-hit') === hit) return;
    this.#hideValue();
  };

  /** Tap shows the value on touch devices (no hover); it goes away on the next tap elsewhere. @param {MouseEvent} event */
  #onClick = (event) => {
    const hit = this.#hitFromEvent(event);
    if (hit) this.#showValue(hit);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#hideValue();
  };

  /** @param {Element} hit */
  #showValue(hit) {
    const index = Number(hit.getAttribute('data-i'));
    const row = this.#rows[index];
    const barRect = this.querySelectorAll('.sci-chart-bars rect')[index]?.getBoundingClientRect();
    if (!row || !barRect) return;
    const unit = this.unit;
    const value =
      row.high === undefined
        ? `${formatNumber(row.low)} ${esc(unit)}`
        : `${formatNumber(row.low)}–${formatNumber(row.high)} ${esc(unit)}<span>midpoint ${formatNumber(
            (row.low + row.high) / 2
          )} ${esc(unit)}</span>`;
    const tip = getTooltip(this);
    tip.innerHTML = `<strong>${esc(row.label)}</strong>${value}`;
    const hostRect = this.getBoundingClientRect();
    const vertical = this.querySelector('svg')?.classList.contains('is-vertical');
    const x = vertical ? barRect.left + barRect.width / 2 - hostRect.left : barRect.right - hostRect.left;
    const y = vertical ? barRect.top - hostRect.top : barRect.top + barRect.height / 2 - hostRect.top;
    placeTooltip(this, tip, x, y);
    this.querySelectorAll('.sci-chart-hit').forEach((h) => h.classList.toggle('is-active', h === hit));
  }

  #hideValue() {
    const tip = this.querySelector(':scope > .sci-tooltip');
    if (tip instanceof HTMLElement) tip.hidden = true;
    this.querySelectorAll('.sci-chart-hit.is-active').forEach((h) => h.classList.remove('is-active'));
  }

  #maybeRender() {
    const width = Math.round(this.getBoundingClientRect().width);
    if (!width || width === this.#lastWidth) return;
    this.#lastWidth = width;
    this.#render(width);
  }

  get unit() {
    return this.getAttribute('unit') ?? '';
  }

  /**
   * @param {number} width
   */
  #render(width) {
    const rows = this.#rows;
    if (!rows.length) return;
    const unit = this.unit;
    const axisLabel = this.getAttribute('axis-label') ?? '';
    const vertical = width < 600;
    const max = Math.max(...rows.map((r) => r.high ?? r.low)) * 1.04;

    /** @type {SVGSVGElement} */
    let svg = /** @type {SVGSVGElement | null} */ (this.querySelector('svg')) ?? document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', `sci-chart-svg ${vertical ? 'is-vertical' : 'is-horizontal'}`);
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', this.getAttribute('aria-label') ?? axisLabel);
    svg.setAttribute('width', String(width));
    if (!svg.isConnected) this.prepend(svg);

    const fontSize = 13;
    const midpoint = (/** @type {BarRow} */ r) => (r.high === undefined ? r.low : (r.low + r.high) / 2);

    if (!vertical) {
      // Measure label widths so the axis column fits the longest bottle name exactly.
      svg.innerHTML = rows
        .map((r) => `<text class="sci-chart-label" font-size="${fontSize}">${esc(r.label)}</text>`)
        .join('');
      let labelWidth = 0;
      for (const text of svg.querySelectorAll('text')) {
        labelWidth = Math.max(labelWidth, /** @type {SVGTextElement} */ (text).getBBox().width);
      }

      const rowHeight = 52;
      const barThickness = 26;
      const margin = { top: 12, right: 24, bottom: 52, left: Math.ceil(labelWidth) + 18 };
      const plotWidth = Math.max(80, width - margin.left - margin.right);
      const height = margin.top + rows.length * rowHeight + margin.bottom;
      const x = (/** @type {number} */ v) => margin.left + (v / max) * plotWidth;
      const ticks = niceTicks(max, clamp(Math.round(plotWidth / 170), 2, 5));

      const grid = ticks
        .map(
          (t) =>
            `<line class="sci-chart-grid" x1="${x(t)}" x2="${x(t)}" y1="${margin.top}" y2="${
              margin.top + rows.length * rowHeight
            }"/>`
        )
        .join('');
      const tickLabels = ticks
        .map(
          (t) =>
            `<text class="sci-chart-tick" x="${x(t)}" y="${margin.top + rows.length * rowHeight + 18}" text-anchor="middle" font-size="12">${formatNumber(
              t
            )} ${esc(unit)}</text>`
        )
        .join('');
      const bars = rows
        .map((r, i) => {
          const cy = margin.top + i * rowHeight + rowHeight / 2;
          const w = Math.max(2, x(midpoint(r)) - margin.left);
          return `<rect x="${margin.left}" y="${cy - barThickness / 2}" width="${w}" height="${barThickness}" rx="5" fill="${
            r.highlight ? COLORS.barHighlight : COLORS.bar
          }"/>`;
        })
        .join('');
      const ranges = rows
        .map((r, i) => {
          if (r.high === undefined) return '';
          const cy = margin.top + i * rowHeight + rowHeight / 2;
          return `<line class="sci-chart-range" x1="${x(r.low)}" x2="${x(r.high)}" y1="${cy}" y2="${cy}"/>`;
        })
        .join('');
      const labels = rows
        .map((r, i) => {
          const cy = margin.top + i * rowHeight + rowHeight / 2;
          return `<text class="sci-chart-label${r.highlight ? ' is-highlight' : ''}" x="${margin.left - 12}" y="${cy}" dominant-baseline="central" text-anchor="end" font-size="${fontSize}">${esc(
            r.label
          )}</text>`;
        })
        .join('');
      const axis = `<line class="sci-chart-axis" x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${
        margin.top + rows.length * rowHeight
      }"/>`;
      const axisTitle = `<text class="sci-chart-axis-title" x="${margin.left + plotWidth / 2}" y="${height - 8}" text-anchor="middle" font-size="12">${esc(
        axisLabel
      )}</text>`;

      const hits = rows
        .map(
          (_, i) =>
            `<rect class="sci-chart-hit" data-i="${i}" x="0" y="${margin.top + i * rowHeight}" width="${width}" height="${rowHeight}"/>`
        )
        .join('');

      svg.setAttribute('height', String(height));
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.innerHTML = `${grid}<g class="sci-chart-bars" style="transform-origin:${margin.left}px 0px">${bars}</g><g class="sci-chart-ranges">${ranges}</g>${axis}${labels}${tickLabels}${axisTitle}<g class="sci-chart-hits">${hits}</g>`;
    } else {
      const height = 440;
      const margin = { top: 12, right: 12, bottom: 82, left: 54 };
      const plotWidth = width - margin.left - margin.right;
      const plotHeight = height - margin.top - margin.bottom;
      const slot = plotWidth / rows.length;
      const barThickness = Math.min(34, slot * 0.55);
      const y = (/** @type {number} */ v) => margin.top + plotHeight - (v / max) * plotHeight;
      const ticks = niceTicks(max, clamp(Math.round(plotHeight / 110), 2, 5));
      const maxChars = Math.max(6, Math.floor(slot / 6.2));

      /** @param {string} label */
      const wrap = (label) => {
        const words = label.split(' ');
        /** @type {string[]} */
        const lines = [];
        let current = '';
        for (const word of words) {
          const next = current ? `${current} ${word}` : word;
          if (next.length > maxChars && current) {
            lines.push(current);
            current = word;
          } else current = next;
        }
        if (current) lines.push(current);
        return lines.slice(0, 3);
      };

      const grid = ticks
        .map((t) => `<line class="sci-chart-grid" x1="${margin.left}" x2="${width - margin.right}" y1="${y(t)}" y2="${y(t)}"/>`)
        .join('');
      const tickLabels = ticks
        .map(
          (t) =>
            `<text class="sci-chart-tick" x="${margin.left - 8}" y="${y(t)}" dominant-baseline="central" text-anchor="end" font-size="11">${formatNumber(
              t
            )} ${esc(unit)}</text>`
        )
        .join('');
      const bars = rows
        .map((r, i) => {
          const cx = margin.left + slot * (i + 0.5);
          const top = y(midpoint(r));
          const h = Math.max(2, margin.top + plotHeight - top);
          return `<rect x="${cx - barThickness / 2}" y="${top}" width="${barThickness}" height="${h}" rx="5" fill="${
            r.highlight ? COLORS.barHighlight : COLORS.bar
          }"/>`;
        })
        .join('');
      const ranges = rows
        .map((r, i) => {
          if (r.high === undefined) return '';
          const cx = margin.left + slot * (i + 0.5);
          return `<line class="sci-chart-range" x1="${cx}" x2="${cx}" y1="${y(r.low)}" y2="${y(r.high)}"/>`;
        })
        .join('');
      const labels = rows
        .map((r, i) => {
          const cx = margin.left + slot * (i + 0.5);
          const lines = wrap(r.label);
          const tspans = lines
            .map((line, li) => `<tspan x="${cx}" dy="${li === 0 ? 0 : 13}">${esc(line)}</tspan>`)
            .join('');
          return `<text class="sci-chart-label${r.highlight ? ' is-highlight' : ''}" x="${cx}" y="${
            margin.top + plotHeight + 16
          }" text-anchor="middle" font-size="11">${tspans}</text>`;
        })
        .join('');
      const axis = `<line class="sci-chart-axis" x1="${margin.left}" x2="${width - margin.right}" y1="${
        margin.top + plotHeight
      }" y2="${margin.top + plotHeight}"/>`;
      const axisTitle = `<text class="sci-chart-axis-title" transform="translate(12 ${
        margin.top + plotHeight / 2
      }) rotate(-90)" text-anchor="middle" font-size="12">${esc(axisLabel)}</text>`;

      const hits = rows
        .map(
          (_, i) =>
            `<rect class="sci-chart-hit" data-i="${i}" x="${margin.left + slot * i}" y="0" width="${slot}" height="${height}"/>`
        )
        .join('');

      svg.setAttribute('height', String(height));
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.innerHTML = `${grid}<g class="sci-chart-bars" style="transform-origin:0px ${
        margin.top + plotHeight
      }px">${bars}</g><g class="sci-chart-ranges">${ranges}</g>${axis}${labels}${tickLabels}${axisTitle}<g class="sci-chart-hits">${hits}</g>`;
    }
  }
}

if (!customElements.get('science-bar-chart-component')) {
  customElements.define('science-bar-chart-component', ScienceBarChart);
}

/* -------------------------------------------------------------------------- */
/* Cumulative carbon model                                                    */
/* -------------------------------------------------------------------------- */

/** Reference footprints in kg CO2e (see the methodology note on the page). */
export const INPUTS = {
  frugal: { ref: 0.092, low: 0.089, high: 0.092 },
  pouch: { ref: 0.071, low: 0.05325, high: 0.071 },
  glass: { ref: 0.492, low: 0.399, high: 0.614 },
  ceramic: { ref: 2.11, low: 2.11, high: 3.305 },
  plastic: { ref: 0.144, low: 0.104, high: 0.19 },
  aluminium: { ref: 0.3525, low: 0.3525, high: 2.354 },
};

export const META = {
  displayHorizonsMonths: { glass: 60, plastic: 36, ceramic: 180 },
  consumptionIntervalMonths: 1.5,
  plasticBottleLifeMonths: 4.5,
  aluminiumFirstPurchaseMonth: 1.5,
  aluminiumRefillIntervalMonths: 5,
};

/** First month after which the refill system stays below the Frugal Bottle baseline (null = never). */
export const CROSSOVERS = {
  glass: { pouch: { referenceMonths: 30, earliestMonths: 12, latestMonths: 45 }, aluminium: { referenceMonths: null } },
  ceramic: { pouch: { referenceMonths: 145.5, earliestMonths: 79.5, latestMonths: 268.5 }, aluminium: { referenceMonths: null } },
  plastic: { pouch: { referenceMonths: null }, aluminium: { referenceMonths: null } },
};

/**
 * @typedef {'glass' | 'ceramic' | 'plastic'} View
 * @typedef {'ref' | 'low' | 'high'} Bound
 * @typedef {{ x: number[], ref: number[], low: number[], high: number[] }} Curve
 */

/** Round away float drift in month arithmetic (all events sit on a 0.5-month grid). */
const month = (/** @type {number} */ m) => Math.round(m * 2) / 2;

/**
 * Deterministic screening model. Each series is a list of purchase events; the curve is the
 * cumulative footprint after each event, evaluated for the ref/low/high inputs.
 *
 * - frugal:     a new Frugal Bottle every consumption interval, starting at month 0.
 * - pouch:      the reusable vessel at month 0, then a refill pouch every interval. The plastic
 *               vessel is additionally replaced every `plasticBottleLifeMonths`.
 * - aluminium:  the reusable vessel at month 0, then a 2.5 L aluminium container at month 1.5
 *               and every 5 months after that (plastic vessel replaced as above).
 *
 * @param {View} view
 * @param {typeof INPUTS} [inputs]
 * @param {typeof META} [meta]
 * @returns {{ frugal: Curve, pouch: Curve, aluminium: Curve }}
 */
export function buildCurves(view, inputs = INPUTS, meta = META) {
  const horizon = meta.displayHorizonsMonths[view];
  const interval = meta.consumptionIntervalMonths;
  const vessel = inputs[view];
  const bounds = /** @type {Bound[]} */ (['ref', 'low', 'high']);

  /**
   * @param {Map<number, Array<{ ref: number, low: number, high: number }>>} events
   * @returns {Curve}
   */
  const accumulate = (events) => {
    const x = [...events.keys()].sort((a, b) => a - b);
    /** @type {Curve} */
    const curve = { x, ref: [], low: [], high: [] };
    for (const bound of bounds) {
      let total = 0;
      for (const t of x) {
        for (const item of events.get(t) ?? []) total += item[bound];
        curve[bound].push(Number(total.toFixed(10)));
      }
    }
    return curve;
  };

  /** @param {Map<number, Array<{ ref: number, low: number, high: number }>>} events @param {number} t @param {{ ref: number, low: number, high: number }} item */
  const add = (events, t, item) => {
    const key = month(t);
    if (!events.has(key)) events.set(key, []);
    events.get(key)?.push(item);
  };

  const frugalEvents = new Map();
  for (let t = 0; t <= horizon + 1e-9; t += interval) add(frugalEvents, t, inputs.frugal);

  const pouchEvents = new Map();
  add(pouchEvents, 0, vessel);
  for (let t = interval; t <= horizon + 1e-9; t += interval) add(pouchEvents, t, inputs.pouch);

  const aluminiumEvents = new Map();
  add(aluminiumEvents, 0, vessel);
  for (let t = meta.aluminiumFirstPurchaseMonth; t <= horizon + 1e-9; t += meta.aluminiumRefillIntervalMonths) {
    add(aluminiumEvents, t, inputs.aluminium);
  }

  if (view === 'plastic') {
    for (let t = meta.plasticBottleLifeMonths; t <= horizon + 1e-9; t += meta.plasticBottleLifeMonths) {
      add(pouchEvents, t, vessel);
      add(aluminiumEvents, t, vessel);
    }
  }

  return { frugal: accumulate(frugalEvents), pouch: accumulate(pouchEvents), aluminium: accumulate(aluminiumEvents) };
}

/**
 * Build a step path through (x, y) purchase points: horizontal until the next purchase, then a
 * vertical jump. Corners are softened with short quadratic curves.
 * @param {Array<[number, number]>} points  pixel coordinates
 * @param {number} endX                     pixel x where the last step ends
 * @param {number} radius
 */
function roundedStepPath(points, endX, radius) {
  if (!points.length) return '';
  /** @type {Array<[number, number]>} */
  const vertices = [];
  points.forEach(([px, py], i) => {
    vertices.push([px, py]);
    const next = points[i + 1];
    if (next) vertices.push([next[0], py]);
  });
  const last = points[points.length - 1];
  if (last) vertices.push([endX, last[1]]);

  let d = '';
  for (let i = 0; i < vertices.length; i++) {
    const current = vertices[i];
    if (!current) continue;
    if (i === 0) {
      d += `M${current[0].toFixed(1)} ${current[1].toFixed(1)}`;
      continue;
    }
    const previous = vertices[i - 1];
    const next = vertices[i + 1];
    if (!previous) continue;
    if (!next) {
      d += `L${current[0].toFixed(1)} ${current[1].toFixed(1)}`;
      continue;
    }
    const inLength = Math.hypot(current[0] - previous[0], current[1] - previous[1]);
    const outLength = Math.hypot(next[0] - current[0], next[1] - current[1]);
    const r = Math.min(radius, inLength / 2, outLength / 2);
    if (r <= 0.2) {
      d += `L${current[0].toFixed(1)} ${current[1].toFixed(1)}`;
      continue;
    }
    const inX = current[0] + ((previous[0] - current[0]) / inLength) * r;
    const inY = current[1] + ((previous[1] - current[1]) / inLength) * r;
    const outX = current[0] + ((next[0] - current[0]) / outLength) * r;
    const outY = current[1] + ((next[1] - current[1]) / outLength) * r;
    d += `L${inX.toFixed(1)} ${inY.toFixed(1)}Q${current[0].toFixed(1)} ${current[1].toFixed(1)} ${outX.toFixed(1)} ${outY.toFixed(1)}`;
  }
  return d;
}

/**
 * Plain (un-rounded) step polyline, used for the range bands.
 * @param {Array<[number, number]>} points
 * @param {number} endX
 */
function stepVertices(points, endX) {
  /** @type {Array<[number, number]>} */
  const vertices = [];
  points.forEach(([px, py], i) => {
    vertices.push([px, py]);
    const next = points[i + 1];
    if (next) vertices.push([next[0], py]);
  });
  const last = points[points.length - 1];
  if (last) vertices.push([endX, last[1]]);
  return vertices;
}

const SERIES_LABELS = {
  glass: { frugal: 'Trigly bottle', pouch: 'Glass + pouch refill', aluminium: 'Glass + aluminium refill' },
  ceramic: { frugal: 'Trigly bottle', pouch: 'Ceramic + pouch refill', aluminium: 'Ceramic + aluminium refill' },
  plastic: { frugal: 'Trigly bottle', pouch: 'Plastic + pouch refill', aluminium: 'Plastic + aluminium refill' },
};

const X_TICK_YEARS = { glass: 1, plastic: 1, ceramic: 3 };

/**
 * @typedef {Object} CarbonChartRefs
 * @property {HTMLElement} plot
 * @property {HTMLElement[]} legendItems
 * @property {HTMLButtonElement[]} viewButtons
 * @property {HTMLButtonElement} rangesToggle
 */

/** @extends {Component<CarbonChartRefs>} */
class ScienceCarbonChart extends Component {
  requiredRefs = ['plot', 'rangesToggle'];

  /** @type {View} */
  #view = 'glass';
  #showRanges = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #lastWidth = 0;
  /** @type {number | null} */
  #fadeTimer = null;
  /** Layout of the last render, used to map pointer x back to months. */
  /** @type {{ left: number, width: number, horizon: number, top: number, height: number, curves: ReturnType<typeof buildCurves> } | null} */
  #layout = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.plot.addEventListener('pointermove', this.#onPointerMove);
    this.refs.plot.addEventListener('pointerdown', this.#onPointerMove);
    this.refs.plot.addEventListener('pointerleave', this.#onPointerLeave);
    const initial = this.getAttribute('view');
    if (initial === 'glass' || initial === 'ceramic' || initial === 'plastic') this.#view = initial;
    this.#resizeObserver = new ResizeObserver(() => {
      const width = Math.round(this.refs.plot.getBoundingClientRect().width);
      if (width && width !== this.#lastWidth) {
        this.#lastWidth = width;
        this.#render();
      }
    });
    this.#resizeObserver.observe(this.refs.plot);
    this.#syncControls();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.refs.plot.removeEventListener('pointermove', this.#onPointerMove);
    this.refs.plot.removeEventListener('pointerdown', this.#onPointerMove);
    this.refs.plot.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const layout = this.#layout;
    const svg = this.refs.plot.querySelector('svg');
    if (!layout || !svg) return;
    const svgRect = svg.getBoundingClientRect();
    const px = event.clientX - svgRect.left;
    if (px < layout.left - 8 || px > layout.left + layout.width + 8) {
      this.#onPointerLeave();
      return;
    }
    const months = Math.max(0, Math.min(layout.horizon, ((px - layout.left) / layout.width) * layout.horizon));
    const labels = SERIES_LABELS[this.#view];
    const colorFor = { frugal: COLORS.trigly, pouch: COLORS.pouch, aluminium: COLORS.aluminium };
    /** @type {string[]} */
    const rows = [];
    let guideY = layout.top + layout.height;
    for (const key of /** @type {const} */ (['frugal', 'pouch', 'aluminium'])) {
      const curve = layout.curves[key];
      let value = curve.ref[0] ?? 0;
      curve.x.forEach((m, i) => {
        if (m <= months + 1e-9) value = curve.ref[i] ?? value;
      });
      rows.push(
        `<span class="sci-tooltip__row"><i style="--swatch:${colorFor[key]}"></i>${esc(labels[key])}<b>${value.toFixed(2)} kg</b></span>`
      );
    }
    const years = months / 12;
    const tip = getTooltip(this.refs.plot);
    tip.innerHTML = `<strong>${years < 1 ? `${Math.round(months)} months` : `${years.toFixed(1)} years`}</strong>${rows.join('')}`;
    const guideX = layout.left + (months / layout.horizon) * layout.width;
    const guide = svg.querySelector('.sci-chart-guide');
    if (guide) {
      guide.setAttribute('x1', String(guideX));
      guide.setAttribute('x2', String(guideX));
      guide.setAttribute('y2', String(guideY));
      guide.classList.add('is-visible');
    }
    placeTooltip(this.refs.plot, tip, guideX, layout.top + layout.height * 0.35);
  };

  #onPointerLeave = () => {
    const tip = this.refs.plot.querySelector(':scope > .sci-tooltip');
    if (tip instanceof HTMLElement) tip.hidden = true;
    this.refs.plot.querySelector('.sci-chart-guide')?.classList.remove('is-visible');
  };

  /**
   * @param {{ view?: string }} data
   */
  setView(data) {
    const view = data?.view;
    if (view !== 'glass' && view !== 'ceramic' && view !== 'plastic') return;
    if (view === this.#view) return;
    this.#view = view;
    this.#syncControls();
    this.#renderWithFade();
  }

  toggleRanges() {
    this.#showRanges = !this.#showRanges;
    this.#syncControls();
    this.#renderWithFade();
  }

  #syncControls() {
    const labels = SERIES_LABELS[this.#view];
    for (const button of this.refs.viewButtons ?? []) {
      const active = button.dataset.view === this.#view;
      button.setAttribute('aria-pressed', String(active));
    }
    this.refs.rangesToggle.setAttribute('aria-checked', String(this.#showRanges));
    for (const item of this.refs.legendItems ?? []) {
      const key = /** @type {keyof typeof labels | undefined} */ (item.dataset.series);
      const text = item.querySelector('[data-legend-label]');
      if (key && text) text.textContent = labels[key];
    }
  }

  #renderWithFade() {
    if (prefersReducedMotion()) {
      this.#render();
      return;
    }
    this.refs.plot.classList.add('is-fading');
    if (this.#fadeTimer) window.clearTimeout(this.#fadeTimer);
    this.#fadeTimer = window.setTimeout(() => {
      this.#render();
      this.refs.plot.classList.remove('is-fading');
    }, 140);
  }

  #render() {
    const plot = this.refs.plot;
    const width = Math.round(plot.getBoundingClientRect().width);
    if (!width) return;

    const view = this.#view;
    const curves = buildCurves(view);
    const horizonMonths = META.displayHorizonsMonths[view];
    const horizonYears = horizonMonths / 12;
    const compact = width < 600;
    const height = compact ? 320 : 380;
    const margin = { top: 18, right: compact ? 14 : 22, bottom: 46, left: compact ? 44 : 56 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    // The y-domain follows the reference curves only, so the (huge) aluminium high band is
    // clipped at the top instead of flattening every other line.
    let maxY = 0;
    for (const key of /** @type {const} */ (['frugal', 'pouch', 'aluminium'])) {
      const curve = curves[key];
      maxY = Math.max(maxY, curve.ref[curve.ref.length - 1] ?? 0);
      if (this.#showRanges) {
        const pouchHigh = curves.pouch.high[curves.pouch.high.length - 1] ?? 0;
        maxY = Math.max(maxY, pouchHigh);
      }
    }
    maxY *= 1.06;

    const x = (/** @type {number} */ months) => margin.left + (months / horizonMonths) * plotWidth;
    const y = (/** @type {number} */ kg) => margin.top + plotHeight - (kg / maxY) * plotHeight;
    const endX = x(horizonMonths);

    const yTicks = niceTicks(maxY, compact ? 3 : 4);
    const xTicks = [];
    for (let year = 0; year <= horizonYears + 1e-9; year += X_TICK_YEARS[view]) xTicks.push(year);

    const clipId = `sci-clip-${this.id || 'carbon'}`;
    const grid = yTicks
      .map((t) => `<line class="sci-chart-grid" x1="${margin.left}" x2="${endX}" y1="${y(t)}" y2="${y(t)}"/>`)
      .join('');
    const yLabels = yTicks
      .map(
        (t) =>
          `<text class="sci-chart-tick" x="${margin.left - 8}" y="${y(t)}" text-anchor="end" dominant-baseline="central" font-size="11">${formatNumber(
            t
          )}</text>`
      )
      .join('');
    const xLabels = xTicks
      .map(
        (year) =>
          `<text class="sci-chart-tick" x="${x(year * 12)}" y="${margin.top + plotHeight + 18}" text-anchor="middle" font-size="11">${year}</text>`
      )
      .join('');
    const xTickMarks = xTicks
      .map(
        (year) =>
          `<line class="sci-chart-axis" x1="${x(year * 12)}" x2="${x(year * 12)}" y1="${margin.top + plotHeight}" y2="${
            margin.top + plotHeight + 5
          }"/>`
      )
      .join('');
    const axes = `<line class="sci-chart-axis" x1="${margin.left}" x2="${endX}" y1="${margin.top + plotHeight}" y2="${
      margin.top + plotHeight
    }"/><line class="sci-chart-axis" x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${margin.top + plotHeight}"/>`;
    const axisTitles = `<text class="sci-chart-axis-title" x="${margin.left + plotWidth / 2}" y="${
      height - 6
    }" text-anchor="middle" font-size="12">Years of use</text><text class="sci-chart-axis-title" transform="translate(12 ${
      margin.top + plotHeight / 2
    }) rotate(-90)" text-anchor="middle" font-size="12">Cumulative kg CO₂e</text>`;

    const seriesOrder = /** @type {const} */ (['aluminium', 'pouch', 'frugal']);
    const colorFor = { frugal: COLORS.trigly, pouch: COLORS.pouch, aluminium: COLORS.aluminium };

    let bands = '';
    let lines = '';
    for (const key of seriesOrder) {
      const curve = curves[key];
      /** @param {Bound} bound */
      const toPoints = (bound) =>
        curve.x.map((m, i) => /** @type {[number, number]} */ ([x(m), y(curve[bound][i] ?? 0)]));

      if (this.#showRanges) {
        const high = stepVertices(toPoints('high'), endX);
        const low = stepVertices(toPoints('low'), endX).reverse();
        const d = [...high, ...low].map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`).join('') + 'Z';
        bands += `<path class="sci-chart-band" d="${d}" fill="${colorFor[key]}"/>`;
      }
      lines += `<path class="sci-chart-line" d="${roundedStepPath(toPoints('ref'), endX, 4)}" stroke="${colorFor[key]}"/>`;
    }

    let marker = '';
    const crossover = CROSSOVERS[view].pouch.referenceMonths;
    if (crossover !== null) {
      const curve = curves.pouch;
      // Value of the refill curve at the crossover month (last purchase at or before it).
      let value = curve.ref[0] ?? 0;
      curve.x.forEach((m, i) => {
        if (m <= crossover + 1e-9) value = curve.ref[i] ?? value;
      });
      const cx = x(crossover);
      const cy = y(value);
      const years = crossover / 12;
      const label = `≈${Number.isInteger(years) ? years : years.toFixed(1)} years`;
      const labelX = cx + 12;
      const anchor = labelX + 80 > endX ? 'end' : 'start';
      const tx = anchor === 'end' ? cx - 12 : labelX;
      marker = `<g class="sci-chart-marker"><line x1="${cx}" x2="${cx}" y1="${cy}" y2="${
        margin.top + plotHeight
      }" stroke-dasharray="3 4"/><circle cx="${cx}" cy="${cy}" r="5"/><text x="${tx}" y="${cy - 10}" text-anchor="${anchor}" font-size="12">${label}</text></g>`;
    }

    this.#layout = { left: margin.left, width: plotWidth, horizon: horizonMonths, top: margin.top, height: plotHeight, curves };
    const guide = `<line class="sci-chart-guide" x1="0" x2="0" y1="${margin.top}" y2="${margin.top + plotHeight}"/>`;

    const tip = plot.querySelector(':scope > .sci-tooltip');
    plot.innerHTML = `<svg class="sci-chart-svg" role="img" aria-label="Cumulative packaging carbon footprint, ${esc(
      view
    )} view" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><defs><clipPath id="${clipId}"><rect x="${margin.left}" y="${margin.top}" width="${plotWidth}" height="${plotHeight}"/></clipPath></defs>${grid}<g clip-path="url(#${clipId})">${bands}${lines}</g>${marker}${axes}${xTickMarks}${xLabels}${yLabels}${axisTitles}${guide}</svg>`;
    if (tip instanceof HTMLElement) {
      tip.hidden = true;
      plot.append(tip);
    }
  }
}

if (!customElements.get('science-carbon-chart-component')) {
  customElements.define('science-carbon-chart-component', ScienceCarbonChart);
}
